import React from 'react';

import { useParams } from 'react-router-dom';

import { BackLink } from '../components/Chrome';
import ContentLoader from '../components/ContentLoader';
import ContentMessage from '../components/ContentMessage';
import ReferenceRail from '../components/ReferenceRail';
import ShadowContent from '../components/ShadowContent';
import { useContentPage } from '../hooks/useContentPage';
import { useSectionRail } from '../hooks/useSectionRail';
import { buildHubHomePath, buildPagePath, normalizeSlug } from '../utils/contentPaths';

const ContentPage: React.FC = () => {
  // Splat route (/content/*) so a full slug path (e.g. "about/committee") is captured —
  // slugFull is a materialised path and contains slashes.
  const params = useParams();
  const slug = params['*'] ?? '';
  const { page, html, state } = useContentPage(slug);
  // Called before any early return — the rail's data must not depend on which
  // branch the page lands in, or the hook order changes between renders.
  const rail = useSectionRail(normalizeSlug(slug));

  /**
   * Back goes up the page tree, not back through history.
   *
   * It names one fixed destination — this page's parent — so it reads the same
   * however the reader got here, and lands in the same place whether they clicked
   * three pages deep or pasted the URL cold. A history control would say "Back"
   * and mean something different on every visit.
   */
  const backTo = rail.parentTo ? buildPagePath(rail.parentTo) : buildHubHomePath();

  const body = ((): React.ReactNode => {
    if (state === 'loading') {
      return <ContentLoader />;
    }

    if (state === 'notfound') {
      return (
        <ContentMessage
          title="Page not found"
          body="The page you are looking for doesn’t exist or isn’t available."
        />
      );
    }

    if (state === 'error') {
      return (
        <ContentMessage
          title="Something went wrong"
          body="This page couldn’t be loaded. Please try again later."
        />
      );
    }

    // Rendered inline rather than in an iframe (a deliberate call — see README), but inside a
    // shadow root so the page's own <style> can't bleed into the app chrome (e.g. the top bar).
    // The HTML is authored by admins in the Unlayer editor, not by visitors.
    if (html) {
      return (
        <div className="content-page">
          <ShadowContent html={html} />
        </div>
      );
    }

    // The page exists but has no content file yet — show what metadata we have.
    return (
      <div className="content-page max-w-measure py-12 sm:py-16">
        <h1 className="font-display text-step-3 leading-[1.05] tracking-[-0.02em] text-ink">
          {page?.title}
        </h1>
        <p className="mt-4 text-step-0 text-ink-muted">
          {page?.description || 'This page has no content yet.'}
        </p>
      </div>
    );
  })();

  // Above both columns, so it sits in the top-left corner of the page rather than
  // over one of them — it acts on the whole view, not on the rail or the article.
  const back = <BackLink to={backTo} label="Back" className="mb-6 mt-8" />;

  // No section claims this page — an unclaimed prefix, or a page reached by a URL
  // outside the nav. It still renders, just without a rail, at full width.
  if (!rail.section) {
    return (
      <>
        {back}
        {body}
      </>
    );
  }

  return (
    <>
      {back}
      {/* Below `lg` the rail follows the content rather than leading it: the page
          is what the member came for. It is not hidden there — that would put the
          depth the top bar no longer walks out of reach entirely on mobile, since
          the burger panel is cut at the same level the bar is. */}
      <div className="flex flex-col gap-12 pb-8 lg:grid lg:grid-cols-[24rem_minmax(0,1fr)] lg:gap-10 xl:grid-cols-[28rem_minmax(0,1fr)]">
        {/* `self-start` is what gives `sticky` room to work in a grid — without it
            the cell stretches to the row's full height and there is nothing to
            stick within. The rail gets its own scroll for the sections deep enough
            to outrun the viewport. */}
        <aside className="order-2 min-w-0 lg:sticky lg:top-8 lg:order-none lg:max-h-[calc(100vh-9rem)] lg:self-start lg:overflow-y-auto">
          <ReferenceRail label={rail.rootTitle} blocks={rail.blocks} loading={rail.loading} />
        </aside>

        <div className="order-1 min-w-0 lg:order-none">{body}</div>
      </div>
    </>
  );
};

export default ContentPage;
